import axios from "axios";
import {
  DEFAULT_ERROR_TITLE,
  ERROR_SESSION_EXPIRED,
  ERROR_BAD_SESSION,
  API_OK,
  API_URL,
} from "./Constants";
import { setLoading } from "./features/common";
import cookie from "js-cookie";

export function decodeRedirectUrl(value) {
  let decoded = value;
  for (let i = 0; i < 3 && /%[0-9a-f]{2}/i.test(decoded); i++) {
    try {
      decoded = decodeURIComponent(decoded);
    } catch {
      break;
    }
  }
  return decoded;
}

export function redirectWithToken(redirectFrom, token) {
  const url = decodeRedirectUrl(redirectFrom);
  const separator = url.includes("?") ? "&" : "?";
  return url + separator + "token=" + token;
}

/**
 * הממשק שאליו נוחתים כשלא ביקשו ממשק מסוים - כתובת, או `null`.
 *
 * מגיע מ-`getResellerData` דרך עוגיית `resellerData`, ולא נבחר כאן: ה-SSO מכריע לפי
 * סדר שכתוב אצלו (`PortalSettings.DEFAULT_PORTAL_ORDER` - ניהול החשבון, ואחריו האזור
 * האישי הסלולרי), כדי שההחלטה הזו לא תישמר בשני מקומות. ריסיילר שאין לו אף אחד
 * מהשניים מגיע עם `null`, והמסך נשאר במקום.
 *
 * העוגייה ולא ה-store, כי הפונקציה הזו נקראת גם מ-`checkValidToken` שאינו רכיב.
 */
export function defaultPortalUrl() {
  try {
    const cached = cookie.get("resellerData");
    const reseller = cached ? JSON.parse(cached) : null;
    return reseller?.defaultPortalUrl || null;
  } catch {
    return null;
  }
}

/**
 * האזור האישי של הריסיילר - כתובת, או `null`.
 *
 * מאותה עוגיית `resellerData`, ומאותו נימוק בדיוק כמו {@link defaultPortalUrl}:
 * הכתובות מחושבות בבילינג מתת-הדומיין של הריסיילר, ונמסרות ב-`portalUrls`. כתובת
 * שהייתה כתובה כאן הייתה אחת לכל הריסיילרים - כלומר לקוח שנשלח לאזור האישי של ספק
 * אחר, שם הטוקן שלו אינו תקף.
 *
 * ⚠️ **`account` ולא `my`**: `my` הוא התווית בכתובת ולא שם הממשק בקטלוג.
 * `null` = לריסיילר אין ממשק ניהול חשבון, והקורא אינו מצייר קישור לשום מקום.
 */
export function accountPortalUrl() {
  try {
    const cached = cookie.get("resellerData");
    const reseller = cached ? JSON.parse(cached) : null;
    const url = reseller?.portalUrls?.account;
    return typeof url === "string" && url ? url : null;
  } catch {
    return null;
  }
}

/**
 * לאן הולכים מיד אחרי התחברות שהצליחה.
 *
 * שלוש אפשרויות, ובסדר הזה:
 *
 *   1. `redirect_from` - הממשק ששלח את הלקוח לכאן. הוא ביקש, והוא מקבל.
 *   2. הממשק שברירת המחדל של הריסיילר, עם הטוקן.
 *   3. `altForward` - המסך הפנימי, כשאין לאן.
 *
 *   השנייה היא מה שלא היה: מי שהגיע ישירות למסך ההתחברות נחת ב"התחברת בהצלחה", מסך
 *   שאינו לשום מקום. עכשיו הוא נכנס לניהול החשבון שלו.
 *
 * במקום אחד ולא בשלושה: אותה החלטה בדיוק חוזרת ב-`Login`, ב-`LoginVerify`
 * וב-`checkValidToken`, ושלושה עותקים הם שלוש הזדמנויות שאחד מהם יישאר מאחור.
 */
export function landAfterLogin(token, navigate, altForward = "/successfully-logged-in") {
  const target = cookie.get("redirect_from") || defaultPortalUrl();
  if (target) {
    window.location.href = redirectWithToken(target, token);
    return;
  }
  if (navigate) navigate(altForward);
}

/**
 * פרטי התמיכה של הריסיילר - טלפון ומייל, או `null` לכל אחד שלא נמסר.
 *
 * מגיע מ-`getResellerData` דרך אותה עוגיית `resellerData` שנושאת את שאר ההגדרות. **אין
 * כאן נפילה לאחור**, ובכוונה: המסך הזה נשען על זה כדי לומר ללקוח למי לפנות, ומספר של
 * ספק אחר הוא תשובה גרועה יותר מלא לומר כלום. הקורא מסתיר את השורה כשאין ערוץ.
 *
 * הערכים מוצגים כפי שנמסרו ואינם מעוצבים מחדש - אותה החלטה כמו במסך "יצירת קשר" של
 * ניהול החשבון: ריסיילר יכול למסור מספר שאינו ישראלי, ועיצוב מקומי היה משבש אותו.
 */
export function supportDetails(reseller) {
  return {
    phone: reseller?.support?.phone || null,
    email: reseller?.support?.email || null,
  };
}

export function formatErrorMessage(errorMessage, t) {
  const translatedErrorMessage = t(errorMessage);
  if (translatedErrorMessage !== errorMessage) {
    errorMessage = errorMessage + " - " + translatedErrorMessage;
  }
  return errorMessage;
}

export function handleApiErrorMessage(
  errorMessage,
  t,
  dispatch,
  showErrorDialog,
  logout,
  navigate,
) {
  const sessionErrors = [ERROR_SESSION_EXPIRED, ERROR_BAD_SESSION];

  if (sessionErrors.includes(errorMessage) && logout !== null) {
    if (
      errorMessage !== ERROR_SESSION_EXPIRED &&
      typeof errorMessage !== "undefined"
    ) {
      cookie.set("logout_error", errorMessage, { sameSite: "strict" });
    }

    dispatch(logout());
    navigate("/", { replace: true });
  } else {
    if (typeof errorMessage !== "undefined") {
      errorMessage = formatErrorMessage(errorMessage, t);
      dispatch(
        showErrorDialog({ title: t(DEFAULT_ERROR_TITLE), text: errorMessage }),
      );
    }
  }
}

export const checkApiSessionErrorMessage = (
  errorMessage,
  dispatch,
  logout,
  navigate,
) => {
  const sessionErrors = [ERROR_SESSION_EXPIRED, ERROR_BAD_SESSION];

  if (sessionErrors.includes(errorMessage) && logout !== null) {
    if (
      errorMessage !== ERROR_SESSION_EXPIRED &&
      typeof errorMessage !== "undefined"
    ) {
      cookie.set("logout_error", errorMessage, { sameSite: "strict" });
    }

    dispatch(logout());
    navigate("/", { replace: true });
  } else {
    return false;
  }
  return true;
};

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * הודעות שהשרת מצרף להן `tryAgainInMinutes`, והנוסח שאומר את הזמן.
 *
 * **רק ההודעות שמגיעות למסך שאין בו ספירה חיה.** `too_many_attempts` (סולם
 * החסימות של ההרשמה ושל השחזור) ו-`lock_login` (נעילה אחרי סיסמאות שגויות) נופלות
 * על מסכים שההודעה היא הערוץ היחיד שלהם. `max_trials_in_period` ו-
 * `max_codes_in_period` אינן כאן במכוון: הן מגיעות למסכי הקודים, ושם
 * `useRetryCountdown` מציג ספירה חיה שמשחררת את הכפתור - וטקסט שאומר "המתן עשר
 * דקות" ליד שעון שמראה 9:58 הוא שני שעונים שאינם מסכימים.
 */
const WAIT_MESSAGE_KEYS = {
  too_many_attempts: "too_many_attempts_wait",
  lock_login: "lock_login_wait",
};

/**
 * משך ההמתנה במלל, מהיחידה הגדולה שמתאימה לו.
 *
 * ספירת דקות אינה מתאימה לכל הטווח: סולם החסימות עולה מדקה אחת עד שבוע, ו"יש
 * להמתין 10080 דקות" הוא מספר שאיש אינו קורא.
 *
 * **מעגלים למעלה, ליחידה שלמה.** כל מדרגה בסולם יוצאת מדויקת בדיוק ברגע החסימה -
 * דקה, חמש, שלושים, שלוש שעות, יממה, שבוע - והעיגול נראה רק באמצע הדעיכה, שם 61
 * דקות שנותרו נאמרות "שעתיים". זו הגזמה מכוונת ולא תקלה: מי שחוזר מוקדם אינו
 * נענש על כך - `assertNotBlocked` אינו רושם סירוב - ולכן הטעות בכיוון הזה עולה
 * המתנה מיותרת, והטעות בכיוון ההפוך עולה ניסיון שנדחה ולקוח שאינו מבין למה.
 *
 * שלוש צורות לכל יחידה - אחת, שתיים, ורבים - כי בעברית "שעתיים" ו"יומיים" אינם
 * "2 שעות". השרת מוסר דקות שבורות (`retrySeconds / 60`), ולכן הקלט מעוגל קודם.
 */
export function formatWaitTime(minutes, t) {
  const forms = (one, two, many, n) =>
    n === 1 ? t(one) : n === 2 ? t(two) : t(many, { n });

  const total = Math.max(1, Math.ceil(Number(minutes) || 0));
  if (total < 60) {
    return forms("a minute", "two minutes", "{{n}} minutes", total);
  }
  const hours = Math.ceil(total / 60);
  if (hours < 24) {
    return forms("an hour", "two hours", "{{n}} hours", hours);
  }
  const days = Math.ceil(hours / 24);
  if (days < 7) {
    return forms("a day", "two days", "{{n}} days", days);
  }
  return forms("a week", "two weeks", "{{n}} weeks", Math.ceil(days / 7));
}

/**
 * הטקסט שמוצג ללקוח על שגיאה מהשרת.
 *
 * מחזיר את מפתח ההודעה כפי שהוא - וזו ההתנהגות שהייתה, כי המסך מתרגם אותו בעצמו -
 * **אלא אם** ההודעה היא אחת מאלה שנושאות זמן המתנה, ואז מחזיר משפט מתורגם ומוכן
 * שכולל אותו. `<Trans i18nKey={...}>` מעביר משפט שאינו מפתח כמו שהוא, ולכן שני
 * המקרים מוצגים באותה שורה בלי שהמסך יבחין ביניהם.
 *
 * למה כאן ולא בכל מסך: שמונה מסכים ממפים שגיאה מהשרת באותן שלוש שורות, ושמונה
 * עותקים הם שמונה הזדמנויות שאחד מהם יישאר בלי הזמן.
 */
export function apiErrorMessage(data, t) {
  const key = data?.message;
  const waitKey = WAIT_MESSAGE_KEYS[key];
  const minutes = Number(data?.tryAgainInMinutes);
  if (!waitKey || !(minutes > 0)) {
    return key;
  }
  return t(waitKey, { wait: formatWaitTime(minutes, t) });
}

export const displayMinutes = (minutes) => {
  const minutesPart = Math.floor(minutes);
  let secondsPart = Math.round((minutes * 60) % 60);
  if (minutesPart === 0 && secondsPart === 0) {
    secondsPart = 1;
  }
  secondsPart = secondsPart < 10 ? `0${secondsPart}` : secondsPart;
  return `${minutesPart}:${secondsPart}`;
};

export async function checkValidToken(
  navigate,
  dispatch,
  logout,
  forward = true,
  invalidLogout = true,
  invalidForward = null,
  altForward = "/successfully-logged-in",
) {
  dispatch(setLoading({ loading: true }));

  const answer = await checkIsTokenValid();
  if (answer.valid) {
    /**
     * סשן תקף שאינו פותח דבר אינו מועבר לממשק - הוא נשלח למסך שמשחרר אותו.
     *
     * לפני כל שאר השיקולים, וגם כש-`forward` כבוי: מסך שנשאר פתוח על סשן חסום הוא
     * מסך שכל בקשה בו תיכשל. זו הנקודה שבה הלופ נשבר.
     */
    const blockedRoute = blockedSessionRoute(answer);
    /**
     * ולא כשזה המסך שאנחנו כבר עליו.
     *
     * המסכים שמשחררים את החסימה קוראים לפונקציה הזו בעצמם, ובלי התנאי הזה כל אחד
     * מהם היה מנווט לעצמו בכל טעינה.
     */
    if (blockedRoute && blockedRoute !== window.location.pathname) {
      dispatch(setLoading({ loading: false }));
      if (navigate) navigate(blockedRoute, { state: { afterLogin: true } });
      return;
    }
    if (forward) {
      const target = cookie.get("redirect_from") || defaultPortalUrl();
      if (target) {
        window.location.href = redirectWithToken(target, cookie.get("token"));
      } else {
        dispatch(setLoading({ loading: false }));
        navigate(altForward);
      }
    } else {
      dispatch(setLoading({ loading: false }));
    }
  } else {
    dispatch(setLoading({ loading: false }));
    if (cookie.get("token")) {
      if (invalidLogout) {
        dispatch(logout());
      }
      if (invalidForward) {
        navigate(invalidForward);
      }
    }
  }
}

export async function setLoadingShow(dispatch) {
  dispatch(setLoading({ loading: true }));
}

export async function setLoadingHide(dispatch) {
  dispatch(setLoading({ loading: false }));
}

export async function checkIsTokenValid() {
  const token = cookie.get("token");
  if (token) {
    const redirectBackUrl = cookie.get("redirect_from");
    let config = { headers: { token: token, lang: getLang() } };
    try {
      const reply = await axios.post(
        API_URL + "isTokenValid",
        { url: redirectBackUrl ?? window.location.href },
        config,
      );
      if (
        reply.hasOwnProperty("data") &&
        reply.data.hasOwnProperty("responseStatus") &&
        reply.data.responseStatus === API_OK
      ) {
        if (reply.data.isTokenValid) {
          /**
           * לא רק "תקף", אלא גם מה שהסשן **חייב** לפני שהוא פותח משהו.
           *
           * הבוליאני שהיה כאן הוא מה שייצר לופ אינסופי: הממשק דוחה בקשה בשער,
           * ה-interceptor מחזיר את הלקוח לכאן, כאן נענה "הטוקן תקף" - וזה נכון - והלקוח
           * מועבר בחזרה לממשק, שדוחה שוב. שאלה נכונה, שאינה השאלה שצריך לשאול.
           */
          return {
            valid: true,
            requires2FAEnrollment: reply.data.requires2FAEnrollment === true,
            requiresContactVerification: reply.data.requiresContactVerification === true,
          };
        }
      }
    } catch (e) {
      return { valid: false };
    }
  }
  return { valid: false };
}

/**
 * המסך שמוציא סשן חסום מהחסימה, או `null` כשהוא אינו חסום.
 *
 * במקום אחד, כי אותה הכרעה נדרשת בכל מסלול שמחזיק טוקן ושואל לאן ללכת. הסדר קבוע
 * ואינו שרירותי: **קודם אמצעי תקשורת** ואחריו רישום שיטת אימות, כי רישום שיטה נשען
 * על יעד שאפשר לשלוח אליו קוד. לקוח שחייב את שניהם עובר כאן פעמיים, ובסדר הזה.
 */
/**
 * מנחית התחברות שהצליחה - או שולח למסך שמשחרר אותה, אם היא חסומה.
 *
 * **נקודה אחת לארבע נקודות הסיום.** התחברות מסתיימת בארבעה מקומות - סיסמה בלבד, קוד
 * אימות, קוד שחזור, ומפתח גישה - וכל אחד מהם קרא ל-`landAfterLogin` ישירות. התוצאה
 * הייתה שכל שער חדש בשרת נדרש להיזכר בארבעתם, ולקוח עם אימות דו-שלבי שחייב לאמת
 * אמצעי תקשורת הונחת בממשק שדחה אותו מיד.
 *
 * `replyData` היא תשובת השרת כמות שהיא; היא נושאת את שני הדגלים בכל אחת מארבע
 * הנקודות (`Authorization.sessionObligations`).
 */
export function landOrResolve(replyData, navigate, altForward = "/successfully-logged-in") {
  const route = blockedSessionRoute({
    valid: true,
    requires2FAEnrollment: replyData?.requires2FAEnrollment === true,
    requiresContactVerification: replyData?.requiresContactVerification === true,
  });
  if (route) {
    navigate(route, { state: { afterLogin: true } });
    return;
  }
  landAfterLogin(replyData?.token, navigate, altForward);
}

export function blockedSessionRoute(answer) {
  if (!answer || answer.valid !== true) {
    return null;
  }
  if (answer.requiresContactVerification) {
    return "/verify-contact";
  }
  if (answer.requires2FAEnrollment) {
    return "/two-step-verification";
  }
  return null;
}

/**
 * The password rule, mirroring the server (models/PasswordPolicy.ts).
 *
 * Both screens - creating a user and resetting a password - used to carry their own
 * regular expression, and the two of them plus the server's did not agree: the screens
 * refused `^`, which the server allowed. One rule in one place is the point.
 *
 * Any character is allowed; what is required is the length and the four kinds. Only
 * control characters and whitespace at the edges are refused - see the server for why.
 */
export function isStrongPassword(password) {
  if (typeof password !== "string") {
    return false;
  }
  const length = Array.from(password).length;
  if (length < 8 || length > 40) {
    return false;
  }
  // eslint-disable-next-line no-control-regex -- refusing control characters is the point
  if (/[\u0000-\u001F\u007F]/.test(password) || password !== password.trim()) {
    return false;
  }
  return (
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^a-zA-Z0-9]/.test(password)
  );
}

/**
 * An address the mail can actually go to, mirroring the server (utils/Helper.ts).
 *
 * Structural only - whether the address exists is answered by the mail we send to it.
 * Both recovery screens used to check nothing but "not empty", so a typo like a
 * missing "@" bought a full round trip and came back as the same "we sent it"
 * message as a real address: the screen could not say the one thing it did know.
 */
export function isValidEmail(email) {
  if (typeof email !== "string" || email.length > 254) {
    return false;
  }
  return /^[^\s@]{1,64}@[^\s@.]+(\.[^\s@.]+)+$/.test(email);
}

/**
 * `iw` הוא הקוד ההיסטורי של עברית, ומערכות הפעלה ודפדפנים ישנים עדיין שולחים אותו.
 * בלעדיו לקוח כזה נחשב "לא עברית", כלומר מקבל אנגלית.
 */
const HEBREW_TAGS = ["he", "iw"];

/** תג שפה לתת-התג הראשי שלו: `he-IL` -> `he`. */
function primarySubtag(tag) {
  return String(tag).trim().toLowerCase().split(/[-_]/)[0];
}

/**
 * השפה הראשונה ברשימת ההעדפות של הדפדפן שהמסכים יודעים להציג.
 *
 * הרשימה כולה ולא `navigator.language` בלבד: דפדפן שמצהיר `["fr-FR","he-IL"]` הצהיר
 * שהוא קורא עברית, וקריאה של הראשון בלבד הייתה שולחת אותו לאנגלית.
 */
function detectBrowserLanguage() {
  const declared = (
    navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language || navigator.userLanguage]
  ).filter(Boolean);

  for (const tag of declared) {
    const primary = primarySubtag(tag);
    if (HEBREW_TAGS.includes(primary)) return "he";
    if (primary === "en") return "en";
  }

  // דפדפן שהצהיר על שפות ואף אחת מהן אינה עברית קורא אנגלית טוב יותר; דפדפן שלא הצהיר
  // כלל אינו אומר דבר, וברירת המחדל היא של רוב הלקוחות.
  return declared.length ? "en" : "he";
}

/**
 * השפה של המסך: הבחירה ששמורה, ואם אין - זו של הדפדפן.
 *
 * זו גם השפה שנשלחת בכותרת `lang` בכל קריאה, ולכן היא **גם** השפה שההודעות יוצאות
 * בה ושנזרעת ללקוח חדש בשרת (`users_preferences`). מכאן החשיבות שהיא תסכים עם מה
 * שמצויר על המסך - ראה `src/i18n/config.js`, שעולה איתה ולא בעברית קשיחה.
 */
export function getLang() {
  try {
    const stored = localStorage.getItem("lang");
    if (stored === "he" || stored === "en") {
      return stored;
    }
  } catch {
    // דפדפן שחוסם אחסון (גלישה פרטית, חסימת אתר). הדפדפן עדיין יודע לומר את שלו,
    // ונפילה כאן הייתה מפילה כל קריאה לשרת - הכותרת נשלחת מכאן.
  }
  return detectBrowserLanguage();
}

/** הכיוון של השפה, לתג `<html>`. */
export function dirOfLang(lang) {
  return HEBREW_TAGS.includes(primarySubtag(lang)) ? "rtl" : "ltr";
}

/**
 * ספרת הביקורת של מספר זיהוי ישראלי - תעודת זהות **ומספר חברה**.
 *
 * אותו אלגוריתם לשניהם, וזו אינה הרחבה אלא עובדה: ח.פ. הוא מספר תשע-ספרתי עם ספרת
 * ביקורת שמחושבת באותה נוסחה של ת.ז. (סכום משוקלל 1-2-1-2, ספרה דו-ספרתית מוחלפת
 * בסכום ספרותיה, והשארית מ-10 חייבת להיות 0). עד כאן המסך בדק ת.ז. בלבד, כלומר כל
 * מספר חברה מומצא עבר את הטופס והגיע לשרת.
 *
 * **המסך אינו השער.** אותה בדיקה בדיוק נעשית ב-SSO (`registerNewUser`) ובבילינג
 * (`IdentityNumbers`), וכאן היא רק כדי לומר ללקוח מיד מה לא בסדר במקום להחזיר אותו
 * מהשרת. הצד היחיד שאי אפשר לבדוק כאן הוא דרכון - אין לו ספרת ביקורת.
 */
export function isValidIsraeliIdNumber(value) {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits || digits.length > 9) {
    return false;
  }
  const padded = digits.padStart(9, "0");
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const step = Number(padded[i]) * ((i % 2) + 1);
    sum += step > 9 ? step - 9 : step;
  }
  return sum % 10 === 0;
}
